import { useEffect, useState } from 'react';
import { clienteService } from '../../services/api';
import '../../styles/Clientes.css';
import { cleanCedula, formatCedula, isValidCedula } from '../../utils/cedulaFormatter';

const Referencias = () => {
  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState('');
  const [referencias, setReferencias] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [mostrarDropdown, setMostrarDropdown] = useState(false);
  const [clienteObj, setClienteObj] = useState(null);

  const [form, setForm] = useState({
    cedula: '',
    nombre: '',
    parentesco: '',
    telefono: '',
    celular: '',
    direccion: '',
    foto: ''
  });

  useEffect(() => {
    loadClientes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.field')) {
        setMostrarDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadClientes = async () => {
    try {
      const resp = await clienteService.getClientes({ per_page: 1000 });
      setClientes(resp.data?.clientes || []);
    } catch (e) {
      setError('No se pudo cargar la lista de clientes');
    }
  };

  const loadReferencias = async (clienteId) => {
    if (!clienteId) return;
    setLoading(true);
    try {
      const resp = await clienteService.getReferencias(clienteId);
      setReferencias(resp.data || []);
    } catch (e) {
      setError('No se pudieron cargar las referencias');
    } finally {
      setLoading(false);
    }
  };

  const clientesFiltrados = clientes.filter(cliente => {
    const termino = busquedaCliente.toLowerCase().trim();
    if (!termino) return true;

    return (
      (cliente.nombres && cliente.nombres.toLowerCase().includes(termino)) ||
      (cliente.apellidos && cliente.apellidos.toLowerCase().includes(termino)) ||
      (cliente.cedula && cliente.cedula.includes(termino)) ||
      (cliente.apodo && cliente.apodo.toLowerCase().includes(termino)) ||
      cliente.id.toString().includes(termino) ||
      (cliente.nombres && cliente.apellidos &&
       `${cliente.nombres} ${cliente.apellidos}`.toLowerCase().includes(termino))
    );
  }).slice(0, 10);

  const seleccionarCliente = (cliente) => {
    setClienteObj(cliente);
    setBusquedaCliente(`${cliente.nombres} ${cliente.apellidos} - ${cliente.cedula}`);
    setMostrarDropdown(false);
    setSelectedCliente(cliente.id);
  };

  const limpiarCliente = () => {
    setClienteObj(null);
    setSelectedCliente('');
    setBusquedaCliente('');
    setReferencias([]);
  };

  useEffect(() => {
    if (selectedCliente) {
      loadReferencias(selectedCliente);
    }
  }, [selectedCliente]);

  const normalizeJceData = (data) => {
    const normalized = {
      nombre: '',
      cedula: ''
    };

    if (data.nombres) {
      normalized.nombre = data.nombres.trim();
    }

    if (data.apellido1 && data.apellido2) {
      const apellidos = `${data.apellido1.trim()} ${data.apellido2.trim()}`;
      normalized.nombre = normalized.nombre
        ? `${normalized.nombre} ${apellidos}`
        : apellidos;
    }

    if (data.cedula) {
      normalized.cedula = data.cedula;
    }

    return normalized;
  };

  const doLookupCedula = async () => {
    setError('');
    setMessage('');

    if (!form.cedula) {
      setError('Ingrese una cédula');
      return;
    }

    if (!isValidCedula(form.cedula)) {
      setError('La cédula debe tener 11 dígitos');
      return;
    }

    setLookupLoading(true);

    try {
      const cedulaLimpia = cleanCedula(form.cedula);

      // Consultar datos de la cédula en JCE
      const response = await clienteService.validarCedula(cedulaLimpia);

      if (response.data) {
        const normalized = normalizeJceData(response.data);
        const fotoUrl = `https://fotospadron2024.blob.core.windows.net/fotos/${cedulaLimpia}.jpg`;

        setForm(prev => ({
          ...prev,
          nombre: normalized.nombre,
          cedula: cedulaLimpia,
          foto: fotoUrl
        }));

        setMessage('Datos cargados exitosamente');
      } else {
        setError('No se encontraron datos para esta cédula');
      }
    } catch (err) {
      console.error('Error en lookup:', err);
      setError(err.response?.data?.error || err.message || 'Error al consultar la cédula');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCliente) {
      setError('Debe seleccionar un cliente');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      await clienteService.addReferencia(selectedCliente, form);
      setMessage('Referencia agregada exitosamente');
      setForm({ cedula: '', nombre: '', parentesco: '', telefono: '', celular: '', direccion: '', foto: '' });
      setShowForm(false);
      loadReferencias(selectedCliente);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar la referencia');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (referenciaId) => {
    if (!window.confirm('¿Está seguro de eliminar esta referencia?')) {
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      await clienteService.deleteReferencia(referenciaId);
      setMessage('Referencia eliminada exitosamente');
      loadReferencias(selectedCliente);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al eliminar la referencia');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="clientes-page">
      <div className="clientes-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <h1>Referencias</h1>
        {selectedCliente && (
          <button
            className="btn primary"
            onClick={() => setShowForm(!showForm)}
            style={{background: '#1976d2', color: '#fff'}}
          >
            {showForm ? 'Cancelar' : '+ Referencia'}
          </button>
        )}
      </div>

      {error && <div className="banner error">{error}</div>}
      {message && <div className="banner ok">{message}</div>}

      <div className="card">
        <div className="field">
          <label>Cliente</label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por nombre, cédula, apodo o ID..."
              value={busquedaCliente}
              onChange={(e) => {
                setBusquedaCliente(e.target.value);
                setMostrarDropdown(true);
              }}
              onFocus={() => setMostrarDropdown(true)}
            />
            {clienteObj && (
              <button
                type="button"
                onClick={limpiarCliente}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6c757d'
                }}
              >
                ×
              </button>
            )}

            {mostrarDropdown && busquedaCliente && clientesFiltrados.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                maxHeight: '400px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                {clientesFiltrados.map((cliente) => (
                  <div
                    key={cliente.id}
                    onClick={() => {
                      seleccionarCliente(cliente);
                      setShowForm(false);
                    }}
                    style={{
                      padding: '10px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    {cliente.foto_url ? (
                      <img
                        src={cliente.foto_url}
                        alt={cliente.nombres}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#007bff',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}>
                        {cliente.nombres?.charAt(0)}{cliente.apellidos?.charAt(0)}
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold' }}>
                        {cliente.nombres} {cliente.apellidos}
                        {cliente.apodo && (
                          <span style={{
                            marginLeft: '8px',
                            color: '#007bff',
                            fontStyle: 'italic',
                            fontWeight: 'normal'
                          }}>
                            ({cliente.apodo})
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        Cédula: {cliente.cedula} • ID: {cliente.id}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showForm && (
        <div className="card" style={{marginTop: '1rem'}}>
          <div className="card-title" style={{background: '#1976d2'}}>Nueva Referencia</div>
          <form onSubmit={handleSubmit}>
            {/* Búsqueda por Cédula */}
            <div style={{marginBottom: '1.5rem', padding: '1rem', background: '#f5f5f5', borderRadius: '4px'}}>
              <div className="grid-2">
                <div className="field">
                  <label>Cédula de la Referencia</label>
                  <input
                    type="text"
                    value={form.cedula}
                    onChange={(e) => {
                      const formatted = formatCedula(e.target.value);
                      setForm({...form, cedula: formatted});
                    }}
                    placeholder="000-0000000-0"
                    maxLength="13"
                  />
                  <small style={{
                    display: 'block',
                    marginTop: '0.25rem',
                    color: isValidCedula(form.cedula) ? '#2e7d32' : '#c62828',
                    fontSize: '0.85rem'
                  }}>
                    {form.cedula ? (
                      isValidCedula(form.cedula)
                        ? '✓ Cédula válida (11 dígitos)'
                        : `✗ Faltan ${11 - cleanCedula(form.cedula).length} dígitos`
                    ) : 'Formato: 000-0000000-0'}
                  </small>
                </div>
                <div className="field" style={{display: 'flex', alignItems: 'flex-end'}}>
                  <button
                    type="button"
                    className="btn"
                    onClick={doLookupCedula}
                    disabled={lookupLoading || !isValidCedula(form.cedula)}
                    style={{background: '#2e7d32', color: '#fff', width: '100%'}}
                  >
                    {lookupLoading ? 'Buscando...' : 'Buscar Cédula'}
                  </button>
                </div>
              </div>

              {/* Foto pequeña de cédula */}
              {form.foto && (
                <div style={{marginTop: '1rem', textAlign: 'center'}}>
                  <img
                    src={form.foto}
                    alt="Foto de cédula"
                    style={{
                      width: '100px',
                      height: '100px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '2px solid #1976d2',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            <div className="grid-2">
              <div className="field">
                <label>Nombre Completo *</label>
                <input
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({...form, nombre: e.target.value})}
                  required
                />
              </div>
              <div className="field">
                <label>Parentesco *</label>
                <select
                  value={form.parentesco}
                  onChange={(e) => setForm({...form, parentesco: e.target.value})}
                  required
                >
                  <option value="">-- Seleccione --</option>
                  <option value="Padre">Padre</option>
                  <option value="Madre">Madre</option>
                  <option value="Hermano/a">Hermano/a</option>
                  <option value="Hijo/a">Hijo/a</option>
                  <option value="Esposo/a">Esposo/a</option>
                  <option value="Amigo/a">Amigo/a</option>
                  <option value="Vecino/a">Vecino/a</option>
                  <option value="Compañero/a de trabajo">Compañero/a de trabajo</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
              <div className="field">
                <label>Teléfono</label>
                <input
                  type="tel"
                  value={form.telefono}
                  onChange={(e) => setForm({...form, telefono: e.target.value})}
                  placeholder="(809) 000-0000"
                />
              </div>
              <div className="field">
                <label>Celular *</label>
                <input
                  type="tel"
                  value={form.celular}
                  onChange={(e) => setForm({...form, celular: e.target.value})}
                  placeholder="(809) 000-0000"
                  required
                />
              </div>
              <div className="field" style={{gridColumn: '1 / -1'}}>
                <label>Dirección</label>
                <textarea
                  value={form.direccion}
                  onChange={(e) => setForm({...form, direccion: e.target.value})}
                  rows="2"
                  placeholder="Dirección completa de la referencia"
                />
              </div>
            </div>
            <div className="form-actions" style={{marginTop: '1rem'}}>
              <button type="submit" className="btn primary" disabled={loading}>
                {loading ? 'Guardando...' : 'Guardar Referencia'}
              </button>
            </div>
          </form>
        </div>
      )}

      {selectedCliente && !loading && (
        <div className="card" style={{marginTop: '1rem'}}>
          <div className="card-title">Lista de Referencias</div>
          {referencias.length === 0 ? (
            <p style={{padding: '1rem', textAlign: 'center', color: '#666'}}>
              No hay referencias registradas para este cliente
            </p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Foto</th>
                  <th>Cédula</th>
                  <th>Nombre</th>
                  <th>Parentesco</th>
                  <th>Teléfono</th>
                  <th>Celular</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {referencias.map((ref) => (
                  <tr key={ref.id}>
                    <td>
                      {ref.foto && (
                        <img
                          src={ref.foto}
                          alt="Foto"
                          style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid #ddd'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                    </td>
                    <td>{ref.cedula || 'N/A'}</td>
                    <td>{ref.nombre}</td>
                    <td>{ref.parentesco}</td>
                    <td>{ref.telefono || 'N/A'}</td>
                    <td>{ref.celular}</td>
                    <td>
                      <button
                        className="btn btn-small"
                        style={{background: '#c62828', color: '#fff', padding: '0.25rem 0.5rem', fontSize: '0.85rem'}}
                        onClick={() => handleDelete(ref.id)}
                        disabled={loading}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default Referencias;
